/**
 * useTaxonomies - Hook to fetch taxonomy terms (categories, tags) for filter dropdowns
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface TaxonomyTerm {
  value: string;
  label: string;
  slug: string;
  count: number;
  parent: number | null;
}

interface TaxonomyResponse {
  taxonomy: string;
  terms: TaxonomyTerm[];
}

async function fetchTaxonomy(siteId: string, taxonomy: 'categories' | 'tags'): Promise<TaxonomyTerm[]> {
  const response = await api.get<TaxonomyResponse>(`/api/v1/sites/${siteId}/taxonomies/${taxonomy}`);
  return response.terms;
}

export function useCategories(siteId: string | undefined) {
  return useQuery({
    queryKey: ['taxonomies', siteId, 'categories'],
    queryFn: () => fetchTaxonomy(siteId!, 'categories'),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useTags(siteId: string | undefined) {
  return useQuery({
    queryKey: ['taxonomies', siteId, 'tags'],
    queryFn: () => fetchTaxonomy(siteId!, 'tags'),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useTaxonomies(siteId: string | undefined) {
  const categories = useCategories(siteId);
  const tags = useTags(siteId);

  return {
    categories: categories.data || [],
    tags: tags.data || [],
    isLoading: categories.isLoading || tags.isLoading,
    error: categories.error || tags.error,
  };
}
